/**
 * Orchestration hook for the native inline checkout island (M2).
 *
 * Owns the page-level checkout state the way checkout/[slug]/page.tsx does, but
 * driven by `window.__CHECKOUT_CONFIG__` (props) instead of the URL — so it can
 * run as a React island on the LP (no iframe). Reuses the SAME currency helpers
 * and (later) the same payment child components + Meta event helpers, so the
 * money path and tracking stay identical to the /checkout page.
 *
 * M2 step 1 = data layer (config, public-config, bump-offers, currency, plans,
 * selection). PaymentIntent + Stripe Elements + PayPal + subscription/trial +
 * Meta events are wired in the next steps.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  convertPsychological,
  formatPrice as fmtPsych,
  fetchCurrencyData,
  getActiveCurrency,
  getCurrencyOverride,
  setCurrencyOverride,
  SUPPORTED_CURRENCIES,
  type CurrencyData,
  type SupportedCurrency,
} from "@/lib/currency";
import { chargeFxRate, productLineCents } from "@/lib/checkout-cart-money";
import { readLpVid } from "@/lib/meta-funnel";
import { consentMetadata } from "@/lib/consent-state";
import {
  fetchRegionalPricing,
  fetchZarRate,
  ZAR_FALLBACK_RATE,
  type SaDisplay,
} from "@/lib/regional-pricing";
import { mergePrefill, type RecoveryPrefill } from "./recovery-deeplink";
import { shownTotalCents, type ServerCharge } from "@/lib/checkout-shown-total";

export interface IslandTierProduct {
  name: string;
  priceCents: number;
  imageUrl?: string;
  originalPriceCents?: number;
  sku?: string;
  /** "subscription" → recurring plan (SubscriptionCheckout path); else one-time. */
  type?: string;
  trialPeriodDays?: number;
  /** This product's own active catalog amounts (currency → minor units, the
   *  one-time rows for a one-time product), from product-info. */
  perCurrencyPrices?: Record<string, number>;
}

export interface IslandConfig {
  slug?: string;
  productId?: string;
  bestProductId?: string;
  landingPageId?: string;
  paypalClientId?: string;
  countdownMinutes?: number;
  tierProductIds?: string[];
  tierProducts?: Record<string, IslandTierProduct>;
  /** USD-cents → { currency → catalog amount } for the LP currency layer. The
   *  island prices plans by tierProducts[id].perCurrencyPrices instead: this
   *  map is keyed by USD amount, so two products at one price collide. */
  perCurrencyByUsd?: Record<string, Record<string, number>>;
  /** Go API base, injected by the LP route (CheckoutConfig.apiUrl) — the island
   *  runs outside Next, so it can't use process.env.NEXT_PUBLIC_API_URL. */
  apiUrl?: string;
  /** Per-LP checkout-section copy (heading + left value recap); all optional —
   *  the island falls back to sensible defaults when unset. */
  checkoutHeading?: string;
  checkoutSubheading?: string;
  recapTitle?: string;
  recapSubtitle?: string;
  recapRating?: string;
  recapGuarantee?: string;
  recapHighlights?: string[];
  /** Optional real testimonial + guarantee condition shown above the payment
   *  step (display-only). */
  proofQuote?: string;
  proofAuthor?: string;
  guaranteeNote?: string;
}

export interface PublicConfig {
  stripe_publishable_key?: string;
  paypal_client_id?: string;
  paypal_env?: "live" | "sandbox";
  meta_pixel_id?: string;
  meta_pixel_enabled?: boolean;
  checkout_provider?: "payment_intent" | "elements_session";
  workspace_name?: string;
  /** API flag (CHECKOUT_INSTALLMENTS_ENABLED). Gates every installments
   *  surface: the Klarna/Affirm-first payment-method order, Stripe's
   *  "as low as $X/mo" messaging element, and PayPal's Pay Later funding.
   *  Absent/false → the checkout renders exactly as it did before. */
  installments_enabled?: boolean;
}

// Matches the /checkout BumpOffer shape byte-for-byte so it passes to the reused
// PayPalSection cleanly (the API returns all fields).
export interface BumpOffer {
  id: string;
  type: string;
  gift_name: string;
  gift_description: string;
  gift_image_url: string;
  addon_price_cents: number;
  original_price_cents: number;
  threshold_cents: number;
  emoji: string;
  discount_type: string;
  discount_value: number;
}

export interface PlanOption {
  id: string;
  name: string;
  type: string;
  price_cents: number;
  original_price_cents?: number;
  trial_period_days?: number;
  per_currency_prices?: Record<string, number>;
}

export interface PiData {
  client_secret?: string;
  payment_intent_id?: string;
  order_id?: string;
  // ConfirmationToken (ctMode) path: the eager call defers the PI (defer_pi),
  // so the response carries the order's amount (drives the deferred <Elements>)
  // but no client_secret — the PI is minted server-side on submit.
  amount_cents?: number;
}

interface LpGlobals {
  __lpVid?: string;
  __lpAdIds?: Record<string, string>;
}

// ConfirmationToken deferred-checkout PROTOTYPE gate. CONCLUSION (2026-06-16): the
// approach can't vault mandate-free — Stripe ties the ConfirmationToken's
// setup_future_usage to the Elements' setupFutureUsage, and declaring SFU on the
// Elements renders the save-card mandate. So the gate is EMPTY: every LP uses the
// proven eager clientSecret flow (mandate-free vault via late PATCH) + the cron
// cancels abandoned PIs. The CT scaffolding stays dormant for reference only.
const CT_PROTOTYPE_SLUGS = new Set<string>([]);

export function useCheckoutInline(config: IslandConfig) {
  const apiBase = config.apiUrl || "https://crm-app.makedesignerbags.com";
  const ctMode = CT_PROTOTYPE_SLUGS.has(config.slug || "");
  const firstId = config.productId || config.tierProductIds?.[0] || "";
  const [selectedProductId, setSelectedProductId] = useState(firstId);
  const [publicConfig, setPublicConfig] = useState<PublicConfig | null>(null);
  const [bumpOffers, setBumpOffers] = useState<BumpOffer[]>([]);
  const [bumpsReady, setBumpsReady] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState<Set<string>>(new Set());
  const [currencyData, setCurrencyData] = useState<CurrencyData | null>(null);
  const [activeCurrency, setActive] = useState<SupportedCurrency>("USD");

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [billingCountry, setBillingCountry] = useState("");

  // Returning-visitor prefill — same `_co_prefill` key as /checkout, so a
  // buyer who typed on either surface is restored on both. Fill-only: typed
  // values win; the geo effect below keeps its empty-only functional update.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = JSON.parse(window.localStorage.getItem("_co_prefill") || "{}");
      const m = document.cookie.match(/(?:^|; )_lp_profile=([^;]*)/);
      const profile = m ? JSON.parse(decodeURIComponent(m[1])) : {};
      setEmail((cur) => cur || saved.email || profile.email || "");
      setFirstName((cur) => cur || saved.firstName || "");
      setLastName((cur) => cur || saved.lastName || "");
      setBillingCountry((cur) => cur || saved.billingCountry || "");
    } catch {
      /* prefill is best-effort */
    }
  }, []);

  // Recovery-link prefill. Functional updates only, so a buyer who is already
  // typing keeps their input; mergePrefill owns the precedence (token email
  // wins, names/country fill gaps). The 800 ms persist effect below then writes
  // the merged result to _co_prefill, so the next plain visit is prefilled too.
  const applyPrefill = useCallback((p: RecoveryPrefill) => {
    setEmail((cur) => mergePrefill({ email: cur }, { email: p.email }).email || cur);
    setFirstName((cur) => mergePrefill({ firstName: cur }, { firstName: p.firstName }).firstName || cur);
    setLastName((cur) => mergePrefill({ lastName: cur }, { lastName: p.lastName }).lastName || cur);
    setBillingCountry(
      (cur) => mergePrefill({ billingCountry: cur }, { billingCountry: p.billingCountry }).billingCountry || cur
    );
  }, []);

  // Persist for the next visit (abandoned checkouts included), debounced.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        window.localStorage.setItem(
          "_co_prefill",
          JSON.stringify({ email, firstName, lastName, billingCountry })
        );
      } catch {
        /* storage may be unavailable (private mode) */
      }
    }, 800);
    return () => clearTimeout(t);
  }, [email, firstName, lastName, billingCountry]);

  // Plans come straight from the injected config (name/price/original/image per
  // tier) — no product-info fetch needed. Subscription detection is added with
  // the payment wiring; one-time is the default.
  const plans: PlanOption[] = useMemo(() => {
    const ids =
      config.tierProductIds && config.tierProductIds.length
        ? config.tierProductIds
        : config.productId
          ? [config.productId]
          : [];
    return ids.map((id) => {
      const tp = config.tierProducts?.[id];
      return {
        id,
        name: tp?.name || "Plan",
        type: tp?.type === "subscription" ? "subscription" : "one_time",
        price_cents: tp?.priceCents || 0,
        original_price_cents: tp?.originalPriceCents,
        trial_period_days: tp?.trialPeriodDays,
        per_currency_prices: tp?.perCurrencyPrices,
      };
    });
  }, [config]);

  // Currency: geo-detect + honour a stored override (same as /checkout). The
  // same geo response carries the visitor's country, so prefill the billing
  // country (only if the buyer hasn't picked one) — functional update avoids
  // clobbering a value typed before the fetch resolves.
  useEffect(() => {
    fetchCurrencyData()
      .then((data) => {
        setCurrencyData(data);
        setActive(getCurrencyOverride() || getActiveCurrency(data));
        if (data.country) setBillingCountry((prev) => prev || data.country.toUpperCase());
      })
      .catch(() => {});
  }, []);

  // React to the LP's currency switcher: getCurrencyLayerScript dispatches a
  // `currencychange` CustomEvent (detail.currency) when the floating pill is
  // used. Update activeCurrency → re-formats prices + recreates the PI.
  useEffect(() => {
    const onChange = (e: Event) => {
      const c = (e as CustomEvent<{ currency?: string }>).detail?.currency;
      // Only accept a real CHARGE currency. Display-only currencies (e.g. ZAR
      // for the South-Africa PPP funnel) must never become the island's active
      // currency — the charge always settles USD there. The layer already skips
      // dispatching ZAR, but guard here too so a display currency can never leak
      // into the money path.
      if (c && (SUPPORTED_CURRENCIES as string[]).includes(c)) {
        setActive(c as SupportedCurrency);
      }
    };
    window.addEventListener("currencychange", onChange);
    return () => window.removeEventListener("currencychange", onChange);
  }, []);

  // Public config (Stripe pk / PayPal / pixels) + bump offers, per selected plan.
  useEffect(() => {
    if (!selectedProductId) return;
    let cancelled = false;
    setBumpsReady(false);
    fetch(`${apiBase}/api/v1/checkout/public-config?product_id=${selectedProductId}`)
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled) setPublicConfig((j.data || j) as PublicConfig);
      })
      .catch(() => {});
    fetch(`${apiBase}/api/v1/checkout/bump-offers?product_id=${selectedProductId}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        setBumpOffers((j.data || []) as BumpOffer[]);
        setSelectedAddons(new Set());
        setBumpsReady(true);
      })
      .catch(() => {
        if (!cancelled) setBumpsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProductId]);

  const rates = currencyData?.rates || { USD: 1 };
  // A plan line in the active currency, priced by THAT plan's product
  // (lib/checkout-cart-money.ts, the same rule as /checkout and the server's
  // per-line charge): its own catalog amount, else its own FX. Never looked up
  // by USD amount: perCurrencyByUsd showed an uncatalogued plan at another
  // product's curated price whenever the two shared a USD price, while the
  // server charges it at FX. USD passes through.
  const planCents = useCallback(
    (plan: PlanOption | undefined): number =>
      plan ? productLineCents(plan, activeCurrency, rates) : 0,
    [activeCurrency, rates],
  );

  // ── South Africa (ZAR) display — resolved NATIVELY via the shared module ───
  // The island renders its own ZAR (owner-curated per product, FX per add-on/
  // incidental) instead of letting the LP currency-layer scanner rewrite its
  // DOM: the scanner substitutes amount-by-amount and cannot keep composite
  // totals consistent (rewritten lines R349+R149 next to an FX'd "R917" total —
  // owner report 2026-07-09). The island root carries data-no-currency so the
  // scanner never touches it. Charge is unaffected: activeCurrency stays USD
  // for ZA visitors and the server's trusted-geo gate bills the reduced USD.
  const [sa, setSa] = useState<SaDisplay | null>(null);
  const planIdsKey = plans.map((p) => p.id).join(",");
  useEffect(() => {
    if (!planIdsKey) return;
    let cancelled = false;
    fetchRegionalPricing(apiBase, planIdsKey.split(",")).then(async (r) => {
      if (!r || cancelled) return;
      const rate = await fetchZarRate(apiBase);
      if (!cancelled) setSa({ ...r, rate });
    });
    return () => {
      cancelled = true;
    };
  }, [planIdsKey, apiBase]);
  const saActive = !!sa?.active;
  const zarRate = sa?.rate || ZAR_FALLBACK_RATE;
  // FX-ZAR for incidental amounts (add-ons, savings, gift gap) — same
  // psychological rounding as every other conversion.
  const saFx = useCallback(
    (usdCents: number) => convertPsychological(usdCents, "ZAR", { ZAR: zarRate }),
    [zarRate],
  );
  // USD-cents → owner-curated ZAR, keyed by each plan's LIST price AND reduced
  // charge (mirrors the /checkout page) so plan lines hit the curated amount.
  const saZarByUsd = useMemo(() => {
    const m = new Map<number, number>();
    if (sa?.active) {
      for (const p of plans) {
        const zar = sa.zarByPid[p.id];
        if (!zar) continue;
        if (p.price_cents) m.set(p.price_cents, zar);
        const chg = sa.usdByPid[p.id];
        if (chg) m.set(chg, zar);
      }
    }
    return m;
  }, [sa, plans]);
  // A plan's displayed price (minor units of the display currency). SA mode
  // shows ZAR: the curated amount keyed by the plan's list/charge price, else FX.
  const planDisplayCents = useCallback(
    (plan: PlanOption | undefined): number => {
      if (!plan) return 0;
      if (saActive) return saZarByUsd.get(plan.price_cents) ?? saFx(plan.price_cents);
      return planCents(plan);
    },
    [saActive, saZarByUsd, saFx, planCents],
  );
  const fmtPlan = useCallback(
    (plan: PlanOption | undefined) => fmtPsych(planDisplayCents(plan), saActive ? "ZAR" : activeCurrency),
    [planDisplayCents, saActive, activeCurrency],
  );
  // TARGET-cents → formatted string (no conversion). For amounts already
  // composed per-line in the display currency (totalDisplayCents): re-converting
  // the USD sum as ONE number yields a THIRD total that matches neither the
  // lines nor the charge (£39+£17 lines, "£48" button, £64 PI — Dina incident).
  const fmtTarget = useCallback(
    (targetCents: number) => fmtPsych(targetCents, saActive ? "ZAR" : activeCurrency),
    [saActive, activeCurrency],
  );
  // ---- Compare-at + savings (display-only money) ---------------------------
  // A compare-at ("regular") price is NEVER charged, so it must not borrow
  // another product's curated catalog amount. perCurrencyByUsd is keyed by USD
  // amount ALONE, and $97 is both the Fundamentals compare-at and the Bundle's
  // real price, so the anchor rendered the Bundle's curated CA$129 instead of
  // an FX CA$137 (owner screenshot 2026-08-05). Outside SA a compare-at is
  // always FX; SA keeps its existing curated behaviour untouched.
  const toCompareCents = useCallback(
    (usdCents: number): number => {
      if (saActive) return saZarByUsd.get(usdCents) ?? saFx(usdCents);
      if (activeCurrency === "USD") return usdCents;
      return convertPsychological(usdCents, activeCurrency, rates);
    },
    [saActive, saZarByUsd, saFx, activeCurrency, rates],
  );
  const fmtCompare = useCallback(
    (usdCents: number) => fmtPsych(toCompareCents(usdCents), saActive ? "ZAR" : activeCurrency),
    [toCompareCents, saActive, activeCurrency],
  );
  // A saving is the difference of the two DISPLAYED figures, never the
  // conversion of the USD difference: the price comes from the catalog and the
  // compare-at from FX, so fmt(orig - price) contradicts the two amounts beside
  // it in every non-USD currency (a "Save CA$70" badge next to a CA$129/CA$63
  // pair whose real gap is CA$66).
  const planSaveCents = useCallback(
    (plan: PlanOption | undefined) =>
      plan ? Math.max(0, toCompareCents(plan.original_price_cents || 0) - planDisplayCents(plan)) : 0,
    [toCompareCents, planDisplayCents],
  );
  const fmtPlanSave = useCallback(
    (plan: PlanOption | undefined) => fmtPsych(planSaveCents(plan), saActive ? "ZAR" : activeCurrency),
    [planSaveCents, saActive, activeCurrency],
  );

  // Add-on amounts: plain FX (saFx in SA), NEVER the catalog map. `fmt` keys
  // the curated price by USD AMOUNT, so a $17 add-on beside a $17 course with a
  // curated £14 rendered £14 while totalDisplayCents and the server charge it
  // at FX (£17): the lines stopped summing to the total. Same composition as
  // totalDisplayCents below and the server's per-addon conversion.
  const toAddonCents = useCallback(
    (usdCents: number): number =>
      saActive ? saFx(usdCents) : convertPsychological(usdCents, activeCurrency, rates),
    [saActive, saFx, activeCurrency, rates],
  );
  const fmtAddon = useCallback(
    (usdCents: number) => fmtPsych(toAddonCents(usdCents), saActive ? "ZAR" : activeCurrency),
    [toAddonCents, saActive, activeCurrency],
  );
  const addonSaveCents = useCallback(
    (origUsd: number, priceUsd: number) => Math.max(0, toCompareCents(origUsd) - toAddonCents(priceUsd)),
    [toCompareCents, toAddonCents],
  );
  const fmtAddonSave = useCallback(
    (origUsd: number, priceUsd: number) =>
      fmtPsych(addonSaveCents(origUsd, priceUsd), saActive ? "ZAR" : activeCurrency),
    [addonSaveCents, saActive, activeCurrency],
  );

  const selectedPlan = plans.find((p) => p.id === selectedProductId) || plans[0];
  const addonsUsd = bumpOffers
    .filter((b) => b.type === "addon" && selectedAddons.has(b.id))
    .reduce((s, b) => s + b.addon_price_cents, 0);
  const subtotalUsd = selectedPlan?.price_cents || 0;
  const totalUsd = subtotalUsd + addonsUsd;

  // ── PaymentIntent (one-time path) — body matches /checkout exactly ──────────
  const [piData, setPiData] = useState<PiData | null>(null);
  // What the server last said it charges (PI create, add-on PATCH); the shown total follows it.
  const [serverCharge, setServerCharge] = useState<ServerCharge | null>(null);
  const emailRef = useRef(email);
  emailRef.current = email;
  const firstRef = useRef(firstName);
  firstRef.current = firstName;
  const lastRef = useRef(lastName);
  lastRef.current = lastName;
  const countryRef = useRef(billingCountry);
  countryRef.current = billingCountry;
  // Add-on set read via ref so the PI-creation effect can include any pre-
  // selected add-ons WITHOUT taking selectedAddons as a dependency (which would
  // recreate the PI — and re-key <Elements> — on every add-on toggle).
  const selectedAddonsRef = useRef(selectedAddons);
  selectedAddonsRef.current = selectedAddons;
  // Add-on set the live PI/order amount already reflects. Set after PI creation
  // and after each successful add-on PATCH so the PATCH effect can no-op when the
  // PI is already in sync.
  const appliedAddonKeyRef = useRef<string | null>(null);
  // Bumped after the server PATCHes the PI amount (add-on change) so the payment
  // form refreshes (elements.fetchUpdates) without a remount. Mirrors /checkout.
  const [paymentRefreshToken, setPaymentRefreshToken] = useState(0);
  // True while the add-on PATCH is in flight (debounce + request) — disables the
  // pay button so a click can't confirm the PI at the pre-PATCH amount.
  const [addonSyncing, setAddonSyncing] = useState(false);
  // Set when the add-on re-price PATCH fails: the selection is reverted to the
  // last applied set (so displayed total == PI amount) and this message is shown.
  const [addonError, setAddonError] = useState("");

  const visitorId = useMemo(() => {
    const g = typeof window !== "undefined" ? (window as unknown as LpGlobals) : {};
    return g.__lpVid || readLpVid() || "";
  }, []);
  const freshAdIds = useCallback((): Record<string, string> => {
    const g = typeof window !== "undefined" ? (window as unknown as LpGlobals) : {};
    return {
      ...(g.__lpAdIds || {}),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };
  }, []);

  const addonsKey = useMemo(
    () => [...selectedAddons].sort().join(","),
    [selectedAddons],
  );
  const isOneTime = (selectedPlan?.type || "one_time") === "one_time";

  // Create the PI ONCE per plan / currency. Add-ons are intentionally NOT a
  // dependency: re-creating the PI yields a new client_secret, which re-keys the
  // <Elements> subtree (key={clientSecret}) and remounts it. Combined with any
  // sibling DOM change at the same render, React hits "removeChild: not a child"
  // and the whole checkout blanks. Instead add-on changes are PATCHed onto the
  // SAME PI below (mirrors /checkout). Add-ons selected before the PI exists are
  // folded into the initial amount via selectedAddonsRef so the first PI is
  // already correct. Contact (email/name/country) is read via refs so typing
  // doesn't recreate the PI — confirmPayment carries billing_details.
  useEffect(() => {
    if (!isOneTime || !selectedProductId || !currencyData || !bumpsReady) return;
    const addons0 = selectedAddonsRef.current;
    // Catalog-aware converted total — the SAME composition as totalDisplayCents
    // (plan at its own catalog amount + EACH add-on FX'd individually), sent
    // with the rate behind it (fx_rate) so the server can recompute it exactly.
    // Never a one-number FX of the USD sum: that figure matches neither the
    // displayed lines nor the server's per-line charge.
    const convertedCents =
      planCents(selectedPlan) +
      bumpOffers
        .filter((b) => b.type === "addon" && addons0.has(b.id))
        .reduce((s, b) => s + convertPsychological(b.addon_price_cents || 0, activeCurrency, currencyData.rates), 0);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${apiBase}/api/v1/checkout/payment-intent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // ctMode defers the PI (order only, no incomplete PI); the PI is
            // minted server-side on submit via /checkout/confirm-token.
            defer_pi: ctMode,
            product_ids: [selectedProductId],
            bump_offer_ids: [...addons0],
            quantities: [1],
            landing_page_id: config.landingPageId || "",
            landing_page_slug: config.slug || "",
            visitor_id: visitorId,
            customer_email: emailRef.current,
            customer_first_name: firstRef.current,
            customer_last_name: lastRef.current,
            customer_country: countryRef.current,
            ad_identifiers: freshAdIds(),
            currency: activeCurrency.toLowerCase(),
            amount_override_cents: activeCurrency === "USD" ? 0 : convertedCents,
            fx_rate: chargeFxRate(activeCurrency, currencyData.rates),
            metadata: { slug: config.slug, ...consentMetadata() },
            return_url: `${window.location.origin}/checkout/upsell?session_id={CHECKOUT_SESSION_ID}`,
          }),
        });
        const j = await res.json();
        if (res.ok) {
          setPiData(j.data as PiData);
          if (typeof j.data?.amount_cents === "number") setServerCharge({ cents: j.data.amount_cents, currency: activeCurrency });
          // The fresh PI already reflects these add-ons — record it so the
          // PATCH effect doesn't immediately re-PATCH the same amount.
          appliedAddonKeyRef.current = [...addons0].sort().join(",");
        }
      } catch {
        /* keep prior piData; the buyer can retry */
      }
    }, 600);
    return () => clearTimeout(t);
    // selectedAddons / contact read via refs — intentionally NOT deps, so add-on
    // toggles and typing don't recreate the PI/order.
  }, [selectedProductId, activeCurrency, currencyData, bumpsReady, isOneTime, bumpOffers, selectedPlan, config, visitorId, freshAdIds, apiBase, ctMode, planCents]);

  // Re-price the SAME PI when add-ons change (debounced). PATCH /orders/:id/addons
  // updates the order + Stripe PI amount in place — the client_secret is
  // unchanged, so <Elements> stays mounted (no re-key, no crash). Bumps the
  // refresh token afterwards so the payment form calls elements.fetchUpdates()
  // and the express wallet shows the new amount. Mirrors /checkout's addon PATCH.
  useEffect(() => {
    if (!isOneTime || ctMode) return;
    if (!piData?.order_id || !piData?.payment_intent_id || !currencyData) return;
    if (addonsKey === appliedAddonKeyRef.current) {
      // Already in sync (e.g. right after a PI (re)create) — clear any stale flag.
      setAddonSyncing(false);
      return;
    }
    setAddonSyncing(true);
    setAddonError("");
    const t = setTimeout(async () => {
      // Same catalog-aware per-addon composition as the PI-create effect above.
      const convertedCents =
        planCents(selectedPlan) +
        bumpOffers
          .filter((b) => b.type === "addon" && selectedAddons.has(b.id))
          .reduce((s, b) => s + convertPsychological(b.addon_price_cents || 0, activeCurrency, currencyData.rates), 0);
      // On failure, revert to the last selection the PI amount actually
      // reflects — otherwise the displayed total permanently diverges from
      // what confirmPayment charges (the server amount).
      const revert = () => {
        setSelectedAddons(new Set((appliedAddonKeyRef.current || "").split(",").filter(Boolean)));
        setAddonError("Couldn't update your order — please try again.");
      };
      setServerCharge(null);
      try {
        const res = await fetch(`${apiBase}/api/v1/checkout/orders/${piData.order_id}/addons`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            payment_intent_id: piData.payment_intent_id,
            bump_offer_ids: [...selectedAddons],
            currency: activeCurrency.toLowerCase(),
            amount_override_cents: activeCurrency === "USD" ? 0 : convertedCents,
            fx_rate: chargeFxRate(activeCurrency, currencyData.rates),
          }),
        });
        if (res.ok) {
          const patched = (await res.json().catch(() => null))?.data?.amount_cents;
          if (typeof patched === "number") setServerCharge({ cents: patched, currency: activeCurrency });
          appliedAddonKeyRef.current = addonsKey;
          setPaymentRefreshToken((x) => x + 1);
        } else {
          revert();
        }
      } catch {
        revert();
      } finally {
        setAddonSyncing(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [addonsKey, piData?.order_id, piData?.payment_intent_id, activeCurrency, currencyData, selectedAddons, bumpOffers, selectedPlan, isOneTime, ctMode, apiBase, planCents]);

  // Vault the card for the post-purchase one-click upsell. Mirrors /checkout
  // (page.tsx): a debounced PATCH attaches a reusable Stripe Customer +
  // setup_future_usage to the PaymentIntent. WITHOUT this the inline order's
  // card is never saved, so the Monthly one-click upsell has no payment method
  // to charge ("monthly upsell doesn't work"). Best-effort; never blocks pay.
  const lastPatchedRef = useRef("");
  const vaultCustomer = useCallback(
    async (overrideEmail?: string) => {
      const oid = piData?.order_id;
      const pi = piData?.payment_intent_id;
      if (!oid || !pi) return;
      const finalEmail = (emailRef.current && emailRef.current.trim()) || (overrideEmail || "").trim();
      if (!finalEmail) return;
      try {
        await fetch(`${apiBase}/api/v1/checkout/orders/${oid}/customer`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            payment_intent_id: pi,
            customer_email: finalEmail,
            first_name: firstRef.current,
            last_name: lastRef.current,
            country: countryRef.current,
          }),
        });
      } catch {
        /* best-effort: payment still proceeds via confirmPayment billing_details */
      }
    },
    [piData?.order_id, piData?.payment_intent_id, apiBase],
  );

  // Debounced pre-vault while the buyer types their email (card path), so the PI
  // carries setup_future_usage before they click pay. Express/card also call
  // vaultCustomer imperatively right before confirm to guarantee it.
  useEffect(() => {
    if (!piData?.order_id || !piData?.payment_intent_id) return;
    const signature = JSON.stringify([email, firstName, lastName, billingCountry]);
    if (signature === lastPatchedRef.current) return;
    if (!email.trim()) return;
    const t = setTimeout(() => {
      void vaultCustomer().then(() => {
        lastPatchedRef.current = signature;
      });
    }, 600);
    return () => clearTimeout(t);
  }, [email, firstName, lastName, billingCountry, piData?.order_id, piData?.payment_intent_id, vaultCustomer]);

  // ctMode submit step: post the ConfirmationToken so the server mints + confirms
  // the PI (and vaults the card server-side, mandate-free). Returns the resulting
  // status — the caller handles requires_action (3DS) via stripe.handleNextAction.
  const confirmWithToken = useCallback(
    async (
      confirmationTokenId: string,
      overrideEmail?: string,
    ): Promise<{ status: string; client_secret?: string; error?: string; decline_code?: string }> => {
      const oid = piData?.order_id;
      if (!oid) return { status: "failed", error: "Checkout is not ready yet — please try again." };
      try {
        const res = await fetch(`${apiBase}/api/v1/checkout/confirm-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            order_id: oid,
            confirmation_token_id: confirmationTokenId,
            return_url: `${window.location.origin}/checkout/upsell?order_id=${oid}`,
            product_ids: [selectedProductId],
            bump_offer_ids: [...selectedAddons],
            landing_page_id: config.landingPageId || "",
            landing_page_slug: config.slug || "",
            visitor_id: visitorId,
            customer_email: (overrideEmail || emailRef.current || "").trim(),
            customer_first_name: firstRef.current,
            customer_last_name: lastRef.current,
            customer_country: countryRef.current,
            ad_identifiers: freshAdIds(),
            currency: activeCurrency.toLowerCase(),
            metadata: { slug: config.slug, ...consentMetadata() },
          }),
        });
        const j = await res.json();
        if (!res.ok) return { status: "failed", error: j.error?.message || "We couldn't process the payment." };
        const d = (j.data || {}) as { status?: string; client_secret?: string; error?: string; decline_code?: string };
        return { status: d.status || "failed", client_secret: d.client_secret, error: d.error, decline_code: d.decline_code };
      } catch {
        return { status: "failed", error: "Network error — please try again." };
      }
    },
    [piData?.order_id, selectedProductId, selectedAddons, config, visitorId, freshAdIds, activeCurrency, apiBase],
  );

  const toggleAddon = useCallback((id: string) => {
    setSelectedAddons((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const changeCurrency = useCallback((c: SupportedCurrency) => {
    setCurrencyOverride(c);
    setActive(c);
  }, []);

  // Display-currency total for the Order Total row + pay button: the plan's
  // catalog (or owner-ZAR) amount + EACH selected add-on converted individually.
  // Never a one-number conversion of the USD sum, and never the add-on SUM
  // converted once — psychological rounding is not additive (conv($17)+conv($17)
  // ≠ conv($34)), and the visible lines must sum to exactly this figure.
  const selectedAddonList = bumpOffers.filter(
    (b) => b.type === "addon" && selectedAddons.has(b.id),
  );
  // Add-ons convert via plain FX (convertPsychological), NEVER the catalog: the
  // server converts add-ons the same way (perLineCharge). The plan is priced by
  // its own product (planCents), the same figure sent as amount_override_cents.
  const totalDisplayCents = saActive
    ? (saZarByUsd.get(subtotalUsd) ?? saFx(subtotalUsd)) +
      selectedAddonList.reduce((s, b) => s + saFx(b.addon_price_cents), 0)
    : planCents(selectedPlan) +
      selectedAddonList.reduce(
        (s, b) => s + convertPsychological(b.addon_price_cents, activeCurrency, rates),
        0,
      );
  // The total shown next to Pay: the server's charge when it differs (one-time, non-SA).
  const chargedDisplayCents =
    isOneTime && !saActive ? shownTotalCents(totalDisplayCents, serverCharge, activeCurrency) : totalDisplayCents;
  // Real USD settlement for the SA disclosure row ("Billed in USD ($30)"):
  // reduced plan charge + USD add-ons. Display-only — the server re-derives it.
  const saBilledUsdCents =
    saActive && selectedPlan
      ? (sa?.usdByPid[selectedPlan.id] ?? (selectedPlan.price_cents || 0)) + addonsUsd
      : 0;

  return {
    config,
    apiBase,
    plans,
    selectedProductId,
    setSelectedProductId,
    selectedPlan,
    isOneTime,
    publicConfig,
    bumpOffers,
    selectedAddons,
    toggleAddon,
    currencyData,
    activeCurrency,
    changeCurrency,
    rates,
    fmtPlan,
    fmtTarget,
    fmtCompare,
    fmtPlanSave,
    planSaveCents,
    fmtAddon,
    fmtAddonSave,
    addonSaveCents,
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    billingCountry,
    setBillingCountry,
    applyPrefill,
    addonsUsd,
    subtotalUsd,
    totalUsd,
    totalDisplayCents,
    chargedDisplayCents,
    saActive,
    saBilledUsdCents,
    piData,
    visitorId,
    vaultCustomer,
    ctMode,
    confirmWithToken,
    paymentRefreshToken,
    addonSyncing,
    addonError,
  };
}
